import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiArrowLeft } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import axiosInstance from '../../services/AxiosInstance';
import { getForum, updateForum } from '../../services/ForumService';
import ForumForm from './ForumForm';
import { getForumPermissions } from './forumPermissions';
import '../tasks/addtask.css';

const normalizeApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  if (payload && Array.isArray(payload.branches)) return payload.branches;
  if (payload && Array.isArray(payload.departments)) return payload.departments;
  if (payload && Array.isArray(payload.subDepartments)) return payload.subDepartments;
  return [];
};

const mapForumToForm = (forum, isFavorite) => {
  const members = forum.ForumMembers || [];
  const participants = members
    .filter((m) => m.role === 'participant')
    .map((m) => m.CorporateUser || { id: m.user_id, full_name: 'User' })
    .filter((u) => u.id);

  return {
    title: forum.title || '',
    description: forum.description || '',
    priority: forum.priority || 'Medium',
    start_date: forum.start_date ? String(forum.start_date).slice(0, 10) : '',
    email_notification: !!forum.email_notification,
    is_favorite: !!isFavorite,
    allow_participants_edit: !!forum.allow_participants_edit,
    allow_participants_close: !!forum.allow_participants_close,
    allow_participants_delete: !!forum.allow_participants_delete,
    branch_ids: (forum.ForumBranches || [])
      .map((b) => b.branch_id || b.Branch?.id)
      .filter(Boolean),
    department_ids: (forum.ForumDepartments || [])
      .map((d) => d.department_id || d.Department?.id)
      .filter(Boolean),
    sub_department_ids: (forum.ForumSubDepartments || [])
      .map((s) => s.sub_department_id || s.SubDepartment?.id)
      .filter(Boolean),
    individual_ids: participants.map((u) => u.id),
    selectedIndividuals: participants,
    files: [],
  };
};

const ForumEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [initialValues, setInitialValues] = useState(null);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData?.corporateId) {
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const userParams = new URLSearchParams();
        userParams.append('corporateId', parseInt(userData.corporateId, 10));
        if (userData.branchId) {
          userParams.append('branch', userData.branchId);
        }
        const [forumRes, usersRes, branchesRes, departmentsRes] = await Promise.all([
          getForum(id),
          axiosInstance.get(`/list/corporateuserslist?${userParams.toString()}`),
          axiosInstance.get(`/settings/branches?corporate_id=${userData.corporateId}`),
          axiosInstance.get(`/settings/departments?corporate_id=${userData.corporateId}`),
        ]);

        let subDepartmentsRes;
        try {
          subDepartmentsRes = await axiosInstance.get(`/settings/subDepartments?corporate_id=${userData.corporateId}`);
        } catch {
          subDepartmentsRes = await axiosInstance.get(`/settings/subdepartments?corporate_id=${userData.corporateId}`);
        }

        const forum = forumRes.data.forum;
        const { canEdit } = getForumPermissions(forum, userData);
        if (!canEdit) {
          toast.error('You are not allowed to edit this discussion');
          navigate(`/forum/detail/${id}`);
          return;
        }

        const filteredUsers = (usersRes.data.users || []).filter((u) => u.id !== userData.id);
        setUsers(filteredUsers);
        setBranches(normalizeApiList(branchesRes.data));
        setDepartments(normalizeApiList(departmentsRes.data));
        setSubDepartments(normalizeApiList(subDepartmentsRes.data));
        setExistingAttachments(forumRes.data.attachments || []);
        setInitialValues(mapForumToForm(forum, forumRes.data.is_favorite));
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load forum');
        navigate(`/forum/detail/${id}`);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, navigate]);

  const handleSubmit = async (form) => {
    if (!form.title?.trim()) {
      toast.error('Title is required');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title.trim());
      fd.append('description', form.description || '');
      fd.append('priority', form.priority || 'Medium');
      fd.append('start_date', form.start_date);
      fd.append('email_notification', form.email_notification);
      fd.append('is_favorite', form.is_favorite);
      fd.append('allow_participants_edit', form.allow_participants_edit);
      fd.append('allow_participants_close', form.allow_participants_close);
      fd.append('allow_participants_delete', form.allow_participants_delete);
      fd.append('individuals', JSON.stringify(form.individual_ids.map((userId) => ({ id: userId }))));
      fd.append('branches', JSON.stringify(form.branch_ids.map((branchId) => ({ id: branchId }))));
      fd.append('departments', JSON.stringify(form.department_ids.map((departmentId) => ({ id: departmentId }))));
      fd.append('sub_departments', JSON.stringify(form.sub_department_ids.map((subId) => ({ id: subId }))));
      (form.files || []).forEach((f) => fd.append('files', f));

      const res = await updateForum(id, fd);
      if (res.data.success === false) {
        toast.error(res.data.message || 'Failed to update forum');
        return;
      }
      toast.success('Discussion updated');
      navigate(`/forum/detail/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update forum');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!initialValues) {
    return (
      <div>
        <p className="text-muted">Could not load discussion for editing.</p>
        <button type="button" className="btn btn-link text-danger p-0" onClick={() => navigate(`/forum/detail/${id}`)}>
          Back to discussion
        </button>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        className="back-button"
        onClick={() => navigate(`/forum/detail/${id}`)}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>

      <ForumForm
        onSubmit={handleSubmit}
        onCancel={() => navigate(`/forum/detail/${id}`)}
        users={users}
        branches={branches}
        departments={departments}
        subDepartments={subDepartments}
        submitting={submitting}
        initialValues={initialValues}
        existingAttachments={existingAttachments}
        submitLabel="Update"
      />

      <InternalFooter />
    </div>
  );
};

export default ForumEdit;
